import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useLanguage } from "@/lib/i18n/LanguageContext";
import { listIssueStatuses } from "@/stores/pm/repository";
import {
  buildStatusCatalogue,
  EMPTY_STATUS_CATALOGUE,
  type StatusCatalogue,
} from "@/domains/pm/statuses/statusCatalogue";

/**
 * The status catalogue for one building: the three built-ins plus every custom
 * status any of its projects defines.
 *
 * Fetched per BUILDING even though statuses are scoped per project, because
 * the issues page can show several projects at once and an issue must always
 * resolve its own status. `resolveStatus` narrows by project at render time.
 *
 * While this is loading, callers get the built-ins alone — the same three
 * statuses the app had before custom ones existed — so nothing renders blank.
 */
export function useIssueStatuses(buildingId: number | undefined): {
  catalogue: StatusCatalogue;
  isLoading: boolean;
} {
  const { language } = useLanguage();

  const { data, isLoading } = useQuery({
    queryKey: ["pm-issue-statuses", buildingId],
    queryFn: () => listIssueStatuses(buildingId as number),
    enabled: buildingId !== undefined && Number.isFinite(buildingId),
    staleTime: 60_000,
  });

  const catalogue = useMemo(() => {
    if (!data) {
      return language === "et" ? buildStatusCatalogue([], "et") : EMPTY_STATUS_CATALOGUE;
    }
    return buildStatusCatalogue(data, language);
  }, [data, language]);

  return { catalogue, isLoading };
}
